// frontend/src/components/CashbookForm.js
// Record a single cash movement. Chrome/ink, French.
//
// NOTE: this page used to POST to `${API_URL}/api/finance/add` and GET
// `${API_URL}/api/vehicles`. API_URL already ends in /api, so both 404'd and
// the form never saved anything — corrected to /finance/add and
// /vehicles/assigned, matching the ledger page.

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import {
  Page, Panel, Button, Field, Checkbox, ErrorNote, Note,
  inputClass, selectClass,
} from './ui/Iparc';

const CashbookForm = () => {
  const { token } = useAuth();

  const [form, setForm] = useState({
    type: 'in',
    category: '',
    amount: '',
    reason: '',
    reference: '',
    vehicleId: '',
    date: new Date().toISOString().slice(0, 10),
  });
  // Unsettled = money promised but not yet moved (créance / dette).
  const [pending, setPending] = useState(false);
  const [dueDate, setDueDate] = useState('');

  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  useEffect(() => {
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API_URL}/vehicles/assigned`, h)
      .then(res => setVehicles(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
    axios.get(`${API_URL}/finance/categories`, h)
      .then(res => setCategories(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [token]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setOkMsg('');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/finance/add`, {
        ...form,
        amount: parseFloat(form.amount),
        settlement: pending ? 'pending' : 'settled',
        dueDate: pending && dueDate ? dueDate : undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOkMsg('Écriture enregistrée.');
      setForm(prev => ({
        ...prev, category: '', amount: '', reason: '', reference: '', vehicleId: '',
      }));
      setPending(false);
      setDueDate('');
    } catch (err) {
      setError(err.response?.data?.error || "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title="Écriture financière" subtitle="Gestion" width="narrow">
      {error && <ErrorNote>{error}</ErrorNote>}
      {okMsg && <Note tone="ok">{okMsg}</Note>}

      <Panel title="Nouvelle écriture">
        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Field label="Sens">
              <select name="type" onChange={handleChange} value={form.type} className={selectClass}>
                <option value="in">Entrée (encaissement)</option>
                <option value="out">Sortie (décaissement)</option>
              </select>
            </Field>
            <Field label="Montant (FCFA)" required>
              <input name="amount" type="number" min="0" required onChange={handleChange}
                     value={form.amount} className={inputClass} placeholder="0" />
            </Field>
            <Field label="Catégorie" required hint="Saisissez librement ou choisissez une catégorie existante.">
              <input name="category" list="cashbook-categories" required onChange={handleChange}
                     value={form.category} className={inputClass} placeholder="Ex : vidange, loyer…" />
              <datalist id="cashbook-categories">
                {categories.map(c => <option key={c._id} value={c.name} />)}
              </datalist>
            </Field>
            <Field label="Date">
              <input name="date" type="date" onChange={handleChange} value={form.date} className={inputClass} />
            </Field>
            <Field label="Motif" required className="sm:col-span-2">
              <input name="reason" required onChange={handleChange} value={form.reason}
                     className={inputClass} placeholder="Ex : règlement facture FA-0142" />
            </Field>
            <Field label="Référence (optionnel)">
              <input name="reference" onChange={handleChange} value={form.reference}
                     className={inputClass} placeholder="N° de pièce, reçu…" />
            </Field>
            <Field label="Véhicule (optionnel)">
              <select name="vehicleId" onChange={handleChange} value={form.vehicleId} className={selectClass}>
                <option value="">Aucun</option>
                {vehicles.map(v => (
                  <option key={v._id} value={v._id}>
                    {v.plateNumber} — {v.brand || v.make || ''} {v.model || ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="pt-3.5 border-t border-[#241F1B] flex flex-col gap-3">
            <Checkbox
              label="Non réglé"
              hint="L'argent n'a pas encore bougé — l'écriture compte comme créance ou dette."
              checked={pending}
              onChange={(e) => setPending(e.target.checked)}
            />
            {pending && (
              <Field label="Échéance" className="max-w-[220px]">
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                       className={inputClass} />
              </Field>
            )}
          </div>

          <div>
            <Button as="button" type="submit" variant="primary" disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Enregistrement…' : "Enregistrer l'écriture"}
            </Button>
          </div>
        </form>
      </Panel>
    </Page>
  );
};

export default CashbookForm;
